import { A } from '@solidjs/router';
import { For, Show } from 'solid-js';
import complainceIconEqualHousing from '~/assets/compliance/icon-equal-housing.png';
import complainceIconMLS from '~/assets/compliance/icon-mls.png';
import complainceIconRealtor from '~/assets/compliance/icon-realtor.png';
import { IconFaceBook, IconInstagram, IconTK, IconTwitter, IconYoutube, IconLinkedin, IconPhone, IconMail } from '~/components/ui/Icons';
import { TranslationKeys, useLocalization } from '~/hooks/useLocalization';
import { useText } from '~/hooks/useText';
import type { PresentableCompanyStyle } from '~/presenters/types/PresentableCompanyStyle';

interface FooterProps {
  companyStyling?: PresentableCompanyStyle;
}

export const Footer = (props: FooterProps) => {
  const { t } = useLocalization();

  const text = useText();

  return (
    <div class="mt-[60px] flex w-full flex-col items-center justify-center bg-[#2C2C32] sm:mt-[90px] md:mt-[118px]">
      <div class="flex w-full max-w-container flex-col px-4 xl:px-0">
        <div class="flex w-full flex-col gap-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex w-full flex-col gap-6 border-b border-white/20 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex items-center">
              <A href="/">
                <Show
                  when={props?.companyStyling?.logo}
                  fallback={<div class="w-fit max-w-[200px] text-lg font-bold text-white">{props?.companyStyling?.name}</div>}>
                  <img src={props?.companyStyling?.logo} alt="logo" class="h-[40px] sm:h-[50px]" />
                </Show>
              </A>
            </div>

            <Show when={props?.companyStyling?.socialMedia}>
              <div class="flex items-center gap-4 text-white sm:gap-[20px]">
                <div class="text-sm">{t(TranslationKeys.FOLLOW_US)}:</div>
                <div class="flex flex-wrap items-center gap-1 sm:gap-2">
                  <Show when={props?.companyStyling?.facebook}>
                    <A href={props?.companyStyling?.facebook ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconFaceBook class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.instagram}>
                    <A href={props?.companyStyling?.instagram ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconInstagram class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.linkedIn}>
                    <A href={props?.companyStyling?.linkedIn ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconLinkedin class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.twitter}>
                    <A href={props?.companyStyling?.twitter ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconTwitter class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.youtube}>
                    <A href={props?.companyStyling?.youtube ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconYoutube class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.tikTok}>
                    <A href={props?.companyStyling?.tikTok ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconTK class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>
                </div>
              </div>
            </Show>
          </div>
        </div>

        <div class="flex flex-col gap-6 pb-6 md:flex-row md:items-start md:justify-between">
          <div class="flex flex-col gap-4">
            <div class="flex flex-wrap gap-4">
              <Show when={props?.companyStyling?.complianceIconRealtor || true}>
                <img class="size-12 object-contain sm:size-14" src={complainceIconRealtor} alt="Realtor" />
              </Show>
              <Show when={props?.companyStyling?.complianceIconMls || true}>
                <img class="size-12 object-contain sm:size-14" src={complainceIconMLS} alt="Master Listing Service" />
              </Show>
              <Show when={props?.companyStyling?.equalHouseing || true}>
                <img class="size-12 object-contain sm:size-14" src={complainceIconEqualHousing} alt="Equal Housing Opportunity" />
              </Show>
            </div>

            <Show when={(props.companyStyling?.listedLegalNames.length ?? 0) > 0}>
              <div class="max-w-md rounded-lg bg-[#383840] p-3 text-left">
                <p class="text-sm text-white/40">{props.companyStyling && text(props.companyStyling.legalNamesIntro)}</p>
                <ul class="mt-2 list-disc space-y-0.5 pl-5 text-sm text-white">
                  <For each={props.companyStyling?.listedLegalNames}>{(name) => <li>{name}</li>}</For>
                </ul>
                <p class="mt-2 text-xs text-white/40">{t(TranslationKeys.DBA_ENTITY_MAY_VARY)}</p>
              </div>
            </Show>
          </div>

          <div class="flex max-w-md flex-col gap-3">
            <Show when={props?.companyStyling?.email}>
              <div class="flex items-center gap-3 rounded-lg bg-[#383840] p-3">
                <div class="flex items-center border-r border-white/20 pr-3">
                  <IconMail class="size-5 text-white/40" />
                </div>
                <div class="flex flex-col">
                  <span class="text-sm text-white/40">{t(TranslationKeys.EMAIL)}</span>
                  <a
                    href={`mailto:${props?.companyStyling?.email}`}
                    class="break-all text-sm text-white hover:text-hover-color hover:underline sm:break-normal">
                    {props?.companyStyling?.email}
                  </a>
                </div>
              </div>
            </Show>

            <Show when={props?.companyStyling?.phoneNumber}>
              <div class="flex items-center gap-3 rounded-lg bg-[#383840] p-3">
                <div class="flex items-center border-r border-white/20 pr-3">
                  <IconPhone class="size-5 text-white/40" />
                </div>
                <div class="flex flex-col">
                  <span class="text-sm text-white/40">{t(TranslationKeys.PHONE)}</span>
                  <a href={`tel:${props?.companyStyling?.phoneNumber}`} class="text-sm text-white hover:text-hover-color hover:underline">
                    {props?.companyStyling?.phoneNumber}
                    <span class="pl-1 text-xs lowercase text-gray-400">({t(TranslationKeys.TEXT_ONLY)})</span>
                  </a>
                </div>
              </div>
            </Show>
            <Show when={props?.companyStyling?.leasingPhoneNumber}>
              <div class="flex items-center gap-3 rounded-lg bg-[#383840] p-3">
                <div class="flex items-center border-r border-white/20 pr-3">
                  <IconPhone class="size-5 text-white/40" />
                </div>
                <div class="flex flex-col">
                  <span class="text-sm text-white/40">{t(TranslationKeys.PHONE)}</span>
                  <a
                    href={`tel:${props?.companyStyling?.leasingPhoneNumber}`}
                    class="text-sm text-white hover:text-hover-color hover:underline">
                    {props?.companyStyling?.leasingPhoneNumber}{' '}
                    <span class="pl-1 text-xs lowercase text-gray-400">({t(TranslationKeys.LEASING)})</span>
                  </a>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </div>

      <div class="w-full bg-[#24242B] py-4 pb-20">
        <div class="mx-auto max-w-container px-4 xl:px-0">
          <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div class="text-sm text-white">
              {t(TranslationKeys.ALL_RIGHTS_RESERVED)} - {props?.companyStyling?.name}
            </div>

            <div class="flex flex-wrap gap-4 text-sm text-white/60">
              <A href="/privacy-policy" class="underline hover:text-hover-color">
                {t(TranslationKeys.PRIVACY_POLICY)}
              </A>
              <span class="text-white/20">|</span>
              <A
                href="https://portal.magicdoor.com/terms-of-service.pdf"
                class="underline hover:text-hover-color"
                target="_blank"
                rel="noopener noreferrer">
                {t(TranslationKeys.TERMS_OF_SERVICE)}
              </A>
              <Show when={props?.companyStyling?.equalHouseing || true}>
                <span class="text-white/20">|</span>
                <A
                  href="https://www.hud.gov/helping-americans/fair-housing-act-overview#The_Fair_Housing"
                  class="underline hover:text-hover-color"
                  target="_blank"
                  rel="noopener noreferrer">
                  {t(TranslationKeys.EQUAL_HOUSING_OPPORTUNITY)}
                </A>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
